"use client"

// app/(public)/blogs/page.tsx
// Public blog list page - M+ Properties Design System

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Search, Calendar, Clock, Eye, ArrowRight, Loader2, BookOpen } from "lucide-react"

interface Blog {
  id: string
  slug: string
  title: string
  excerpt: string
  featuredImage: string | null
  publishedAt: string
  metaTitle: string | null
  metaDescription: string | null
}

export default function BlogsPage() {
  const [blogs, setBlogs] = useState<Blog[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  useEffect(() => {
    fetchBlogs()
  }, [page, searchQuery])

  const fetchBlogs = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('limit', '12')
      if (searchQuery) params.append('search', searchQuery)

      const response = await fetch(`/api/blogs?${params.toString()}`)
      const result = await response.json()

      if (result.success) {
        setBlogs(result.data.blogs)
        setTotalPages(result.data.pagination.totalPages)
        setHasMore(result.data.pagination.hasMore)
      }
    } catch (error) {
      console.error('Error fetching blogs:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    fetchBlogs()
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Show nothing if no blogs
  if (!loading && blogs.length === 0 && !searchQuery) {
    return null
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Better Contrast */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white overflow-hidden py-16 md:py-24">
        {/* Diagonal Lines Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, #d60500 35px, #d60500 36px)',
          }}></div>
        </div>

        {/* Red Accent Overlay */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#d60500]/20 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <div className="h-px w-16 bg-[#d60500] mx-auto mb-4"></div>
              <p className="text-sm uppercase tracking-widest text-[#d60500] font-bold mb-3 flex items-center justify-center gap-2">
                <BookOpen className="w-4 h-4" />
                INSIGHTS & EXPERTISE
              </p>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
              Real Estate Blog
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Expert insights, market trends, and luxury property guides
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full h-14 pl-14 pr-32 bg-white border-2 border-gray-300 rounded-sm text-gray-900 placeholder:text-gray-500 focus:border-[#d60500] focus:ring-2 focus:ring-[#d60500]/20 transition-all"
              />
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#d60500] hover:bg-[#b50400] text-white font-bold rounded-sm h-10 px-6"
              >
                Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
              <span className="ml-3 text-lg text-gray-600">Loading articles...</span>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600 mb-6">
                {searchQuery ? 'Try a different search term' : 'Check back soon for new content'}
              </p>
              {searchQuery && (
                <Button
                  onClick={() => {
                    setSearchQuery('')
                    setPage(1)
                  }}
                  variant="outline"
                  className="border-accent text-accent hover:bg-accent hover:text-white"
                >
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Blog Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogs.map((blog) => (
                  <Link
                    key={blog.id}
                    href={`/blogs/${blog.slug}`}
                    className="group"
                  >
                    <article className="bg-white border-2 border-gray-200 rounded-sm overflow-hidden hover:border-accent hover:shadow-xl transition-all duration-300">
                      {/* Featured Image */}
                      <div className="relative h-64 bg-gray-100 overflow-hidden">
                        {blog.featuredImage ? (
                          <Image
                            src={blog.featuredImage}
                            alt={blog.title}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary/80">
                            <BookOpen className="w-16 h-16 text-accent" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        {/* Meta Info */}
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(blog.publishedAt)}</span>
                          </div>
                        </div>

                        {/* Title */}
                        <h2 className="text-xl font-bold text-primary mb-3 group-hover:text-accent transition-colors line-clamp-2">
                          {blog.title}
                        </h2>

                        {/* Excerpt */}
                        {blog.excerpt && (
                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {blog.excerpt}
                          </p>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          <span className="text-sm text-gray-600">
                            Spire Group
                          </span>
                          <div className="flex items-center gap-2 text-accent font-bold text-sm group-hover:gap-3 transition-all">
                            Read More
                            <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center gap-2">
                  <Button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent hover:text-white disabled:opacity-50"
                  >
                    Previous
                  </Button>

                  <div className="flex items-center gap-2 px-4">
                    <span className="text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                  </div>

                  <Button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasMore}
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent hover:text-white disabled:opacity-50"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  )
}
